import { useState } from "react";
import { View, ScrollView, StyleSheet, TextInput, Alert, Platform } from "react-native";
import { router } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { ModernCard } from "@/components/crew";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { DateInput } from "@/components/ui/DateInput";
import { InlineDropdown } from "@/components/ui/InlineDropdown";
import { ToggleSwitch } from "@/components/ui/ToggleSwitch";
import { PhotoCapture, PhotoFile, photoFileToBase64 } from "@/components/PhotoCapture";
import { supabase } from "@/lib/supabase-client";
import useAuth from "@/hooks/useAuth";

const REPORT_TYPE_OPTIONS = [
  { label: "Injury", value: "Injury" },
  { label: "Near Miss", value: "Near Miss" },
  { label: "Illness", value: "Illness" },
];

export default function InjuryReportScreen() {
  const { colors } = useTheme();
  const { displayNames } = useAuth();

  const [reportType, setReportType] = useState("");
  const [atJobsite, setAtJobsite] = useState(false);
  const [incidentDate, setIncidentDate] = useState<Date>(new Date());
  const [witnesses, setWitnesses] = useState("");
  const [location, setLocation] = useState("");
  const [activity, setActivity] = useState("");
  const [leadUp, setLeadUp] = useState("");
  const [bodyPartInjured, setBodyPartInjured] = useState("");
  const [prevention, setPrevention] = useState("");
  const [sawDoctor, setSawDoctor] = useState(false);
  const [previousInjury, setPreviousInjury] = useState(false);
  const [photos, setPhotos] = useState<PhotoFile[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isNearMiss = reportType === "Near Miss";
  const canSubmit = reportType.length > 0 && leadUp.trim().length > 0;

  const handleSubmit = async () => {
    if (!canSubmit || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const photoData = photos.length > 0 ? await Promise.all(photos.map(photoFileToBase64)) : undefined;

      const { data, error } = await supabase.functions.invoke("submit-injury-report", {
        body: {
          report_type: reportType,
          at_jobsite: atJobsite,
          occurred_date: incidentDate.toISOString(),
          witnesses: witnesses.trim() || undefined,
          location: location.trim() || undefined,
          activity: activity.trim() || undefined,
          lead_up: leadUp.trim(),
          body_part_injured: !isNearMiss ? bodyPartInjured.trim() || undefined : undefined,
          prevention: isNearMiss ? prevention.trim() || undefined : undefined,
          saw_doctor: !isNearMiss ? sawDoctor : undefined,
          previous_injury: !isNearMiss ? previousInjury : undefined,
          photos: photoData,
        },
      });

      if (error) {
        console.error("[INJURY] Submit error:", error);
        Alert.alert("Error", error.message || "Failed to submit injury report.");
        return;
      }

      if (data?.success) {
        Alert.alert("Report Submitted", `Injury report ${data.report_number} has been created.`, [
          { text: "OK", onPress: () => router.dismiss() },
        ]);
      } else {
        Alert.alert("Error", data?.error || "Unexpected response from server.");
      }
    } catch (e: any) {
      console.error("[INJURY] Submit exception:", e);
      Alert.alert("Error", e?.message || "Something went wrong.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const jobsiteName = displayNames?.projects?.name || "company";

  return (
    <ScrollView
      style={[styles.container, { backgroundColor: colors.backgroundSecondary }]}
      contentContainerStyle={styles.content}
      keyboardShouldPersistTaps="handled"
    >
      {/* 1. Report Type */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="alert-circle" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Type of Report
          </ThemedText>
        </View>
        <InlineDropdown
          options={REPORT_TYPE_OPTIONS}
          value={reportType}
          onSelect={setReportType}
          placeholder="Select report type..."
        />
      </ModernCard>

      {/* 2. At Jobsite? */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="domain" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Jobsite
          </ThemedText>
        </View>
        <View style={styles.toggleRow}>
          <ThemedText style={styles.toggleLabel}>Did this incident occur at a {jobsiteName} jobsite?</ThemedText>
          <ToggleSwitch value={atJobsite} onValueChange={setAtJobsite} />
        </View>
      </ModernCard>

      {/* 3. Date & Time */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="clock-outline" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Time of Incident
          </ThemedText>
        </View>
        <DateInput value={incidentDate} onChange={setIncidentDate} mode="datetime" label="Date & Time" />
      </ModernCard>

      {/* 4. Witnesses */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="account-group-outline" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Names of Witnesses
          </ThemedText>
        </View>
        <TextInput
          style={[
            styles.searchInput,
            {
              color: colors.text,
              backgroundColor: colors.background,
              borderColor: colors.border,
            },
          ]}
          placeholder="If any"
          placeholderTextColor={colors.placeholder}
          value={witnesses}
          onChangeText={setWitnesses}
        />
      </ModernCard>

      {/* 5. Location */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="map-marker-outline" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Where did this incident occur?
          </ThemedText>
        </View>
        <TextInput
          style={[
            styles.searchInput,
            {
              color: colors.text,
              backgroundColor: colors.background,
              borderColor: colors.border,
            },
          ]}
          placeholder="Location of incident"
          placeholderTextColor={colors.placeholder}
          value={location}
          onChangeText={setLocation}
        />
      </ModernCard>

      {/* 6. Activity */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="run" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            What were you doing at the time?
          </ThemedText>
        </View>
        <TextInput
          style={[
            styles.notesInput,
            {
              color: colors.text,
              backgroundColor: colors.background,
              borderColor: colors.border,
            },
          ]}
          placeholder="Describe what you were doing..."
          placeholderTextColor={colors.placeholder}
          value={activity}
          onChangeText={setActivity}
          multiline
          numberOfLines={4}
          textAlignVertical="top"
        />
      </ModernCard>

      {/* 7. Lead Up */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="format-list-numbered" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Describe step by step what led up to the incident
          </ThemedText>
        </View>
        <TextInput
          style={[
            styles.notesInput,
            {
              color: colors.text,
              backgroundColor: colors.background,
              borderColor: colors.border,
            },
          ]}
          placeholder="Step by step description..."
          placeholderTextColor={colors.placeholder}
          value={leadUp}
          onChangeText={setLeadUp}
          multiline
          numberOfLines={4}
          textAlignVertical="top"
        />
      </ModernCard>

      {/* 8. Body Part Injured (hidden for Near Miss) */}
      {!isNearMiss && (
        <ModernCard style={styles.card} margin={0}>
          <View style={styles.cardHeader}>
            <MaterialCommunityIcons name="human" size={24} color={colors.primary} />
            <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
              What part(s) of your body were injured?
            </ThemedText>
          </View>
          <TextInput
            style={[
              styles.searchInput,
              {
                color: colors.text,
                backgroundColor: colors.background,
                borderColor: colors.border,
              },
            ]}
            placeholder="e.g. Left hand, lower back, right knee"
            placeholderTextColor={colors.placeholder}
            value={bodyPartInjured}
            onChangeText={setBodyPartInjured}
          />
        </ModernCard>
      )}

      {/* 9. Prevention (shown only for Near Miss) */}
      {isNearMiss && (
        <ModernCard style={styles.card} margin={0}>
          <View style={styles.cardHeader}>
            <MaterialCommunityIcons name="shield-alert-outline" size={24} color={colors.primary} />
            <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
              What could have been done to prevent this?
            </ThemedText>
          </View>
          <TextInput
            style={[
              styles.notesInput,
              {
                color: colors.text,
                backgroundColor: colors.background,
                borderColor: colors.border,
              },
            ]}
            placeholder="Describe preventive measures..."
            placeholderTextColor={colors.placeholder}
            value={prevention}
            onChangeText={setPrevention}
            multiline
            numberOfLines={4}
            textAlignVertical="top"
          />
        </ModernCard>
      )}

      {/* 10. Saw Doctor (hidden for Near Miss) */}
      {!isNearMiss && (
        <ModernCard style={styles.card} margin={0}>
          <View style={styles.cardHeader}>
            <MaterialCommunityIcons name="doctor" size={24} color={colors.primary} />
            <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
              Doctor Visit
            </ThemedText>
          </View>
          <View style={styles.toggleRow}>
            <ThemedText style={styles.toggleLabel}>Did you see a doctor about this injury/illness?</ThemedText>
            <ToggleSwitch value={sawDoctor} onValueChange={setSawDoctor} />
          </View>
        </ModernCard>
      )}

      {/* 11. Previous Injury (hidden for Near Miss) */}
      {!isNearMiss && (
        <ModernCard style={styles.card} margin={0}>
          <View style={styles.cardHeader}>
            <MaterialCommunityIcons name="history" size={24} color={colors.primary} />
            <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
              Previous Injury
            </ThemedText>
          </View>
          <View style={styles.toggleRow}>
            <ThemedText style={styles.toggleLabel}>Has this part of your body been injured before?</ThemedText>
            <ToggleSwitch value={previousInjury} onValueChange={setPreviousInjury} />
          </View>
        </ModernCard>
      )}

      {/* 12. Photos */}
      <ModernCard style={styles.card} margin={0}>
        <View style={styles.cardHeader}>
          <MaterialCommunityIcons name="camera" size={24} color={colors.primary} />
          <ThemedText type="subtitle" style={{ marginLeft: 8 }}>
            Photos
          </ThemedText>
        </View>
        <PhotoCapture
          photos={photos}
          onPhotosChange={setPhotos}
          maxPhotos={10}
          placeholder="Tap to add photo"
          disabled={isSubmitting}
        />
      </ModernCard>

      {/* 13. Action Buttons */}
      <View style={styles.buttonRow}>
        <Button
          text="Cancel"
          icon="close"
          iconPosition="left"
          onPress={() => router.dismiss()}
          style={styles.button}
          enabled={!isSubmitting}
        />
        <Button
          text={isSubmitting ? "Submitting..." : "Submit Report"}
          icon="check"
          iconPosition="left"
          onPress={handleSubmit}
          style={[styles.button, { backgroundColor: colors.primary }]}
          textStyle={{ color: "white" }}
          enabled={canSubmit && !isSubmitting}
        />
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 16,
    paddingBottom: 32,
    gap: 16,
  },
  card: {},
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
  },
  toggleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  toggleLabel: {
    fontSize: 14,
    fontWeight: "500",
    flex: 1,
    marginRight: 12,
  },
  searchInput: {
    height: 44,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
    ...(Platform.OS === "web" ? { outlineStyle: "none" as any } : {}),
  },
  notesInput: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    minHeight: 120,
    ...(Platform.OS === "web" ? { outlineStyle: "none" as any } : {}),
  },
  buttonRow: {
    flexDirection: "row",
    gap: 12,
  },
  button: {
    flex: 1,
    height: 48,
  },
});
